import { useState, useCallback } from "react";
import { Users, Footprints, Coins, ShoppingCart, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useOrgContext } from "../context/OrgContext";
import { useLocale } from "../context/LanguageContext";
import {
  useOrganizationDashboard,
  useRefreshOrganizationStats,
  MetricCard,
  PeriodSelector,
  EngagementRateChart,
  StepsChart,
  EconomyVelocityChart,
  ChallengeEventSummary,
  RefreshButton,
  type DashboardPeriod,
} from "../features/dashboard";

export function DashboardPage() {
  const { user } = useAuth();
  const { currentOrg, currentOrgId } = useOrgContext();
  const { t } = useLocale();
  const [period, setPeriod] = useState<DashboardPeriod>("MONTH");

  const { data, loading } = useOrganizationDashboard({
    organizationId: currentOrgId ?? "",
    period,
    skip: !currentOrgId,
  });

  const [refreshStats, { loading: refreshing }] = useRefreshOrganizationStats();

  const handleRefresh = useCallback(() => {
    if (!currentOrgId) return;
    refreshStats({ variables: { organizationId: currentOrgId } });
  }, [currentOrgId, refreshStats]);

  const dashboard = data?.organizationDashboard;
  const current = dashboard?.currentPeriod;
  const trend = dashboard?.trend;
  const daily = dashboard?.dailyBreakdown ?? [];
  const isLoading = loading && !dashboard;
  const isSuppressed = current?._suppressed === true;

  if (!currentOrgId) {
    return (
      <div>
        <h1 className="mb-2 text-2xl font-bold text-foreground">{t("dashboard.title")}</h1>
        <p className="text-muted-foreground">
          {t("dashboard.selectOrgHint")}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("dashboard.title")}</h1>
          <p className="text-muted-foreground">
            {t("dashboard.welcomeBack", { firstName: user?.firstName ?? "" })}
            {currentOrg ? ` — ${currentOrg.name}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <RefreshButton onClick={handleRefresh} loading={refreshing} />
        </div>
      </div>

      {isSuppressed && (
        <div
          role="status"
          className="mb-6 flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/5 p-4"
        >
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
          <div className="flex-1 text-sm">
            <p className="font-medium text-foreground">
              {t("dashboard.suppressed.title")}
            </p>
            <p className="mt-1 text-muted-foreground">
              {t("dashboard.suppressed.body")}
            </p>
          </div>
        </div>
      )}

      {/* Primary KPIs — 4 cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label={t("dashboard.activeMembers")}
          value={
            current
              ? `${current.activeMembers}/${current.totalMembers} (${current.activeMembersPercentage}%)`
              : "—"
          }
          trend={trend?.activeMembersTrend}
          subtitle={t("dashboard.vsPreviousPeriod")}
          icon={<Users className="h-4 w-4" />}
          loading={isLoading}
          suppressed={isSuppressed}
        />
        <MetricCard
          label={t("dashboard.totalSteps")}
          value={current ? formatLargeNumber(current.totalSteps) : "—"}
          trend={trend?.stepsTrend}
          subtitle={t("dashboard.vsPreviousPeriod")}
          icon={<Footprints className="h-4 w-4" />}
          loading={isLoading}
          suppressed={isSuppressed}
        />
        <MetricCard
          label={t("dashboard.koinsEarned")}
          value={current ? formatLargeNumber(current.totalKoinsEarned) : "—"}
          trend={trend?.koinsEarnedTrend}
          subtitle={t("dashboard.vsPreviousPeriod")}
          icon={<Coins className="h-4 w-4" />}
          loading={isLoading}
          suppressed={isSuppressed}
        />
        <MetricCard
          label={t("dashboard.koinsSpent")}
          value={current ? formatLargeNumber(current.totalKoinsSpent) : "—"}
          trend={trend?.koinsSpentTrend}
          subtitle={t("dashboard.vsPreviousPeriod")}
          icon={<ShoppingCart className="h-4 w-4" />}
          loading={isLoading}
          suppressed={isSuppressed}
        />
      </div>

      {/* Secondary — Activities + Challenges/Events summary */}
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <MetricCard
          label={t("dashboard.activitiesLogged")}
          value={current ? current.totalActivities.toLocaleString() : "—"}
          icon={<Footprints className="h-4 w-4" />}
          loading={isLoading}
          suppressed={isSuppressed}
        />
        <div className="sm:col-span-2">
          <ChallengeEventSummary current={current} loading={isLoading} />
        </div>
      </div>

      {/* Charts */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <EngagementRateChart data={daily} loading={isLoading} />
        <StepsChart data={daily} loading={isLoading} />
      </div>

      <div className="mt-6">
        <EconomyVelocityChart data={daily} loading={isLoading} />
      </div>
    </div>
  );
}

function formatLargeNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}
